/*
 * certificate-maker/smart.js — the part that does the thinking.
 *
 * Three jobs, each one a thing the person would otherwise do by hand:
 *
 *   parseCertificate()  A ready certificate arrives as TEXT — pasted out of
 *                       Word, out of a message, off last year's file. Every
 *                       generator makes you retype it into a dozen boxes. This
 *                       reads it and fills them.
 *   paletteFrom()       A school has colours; they are in its crest. Pull them
 *                       out of the uploaded image instead of asking someone to
 *                       name a hex.
 *   preflight()         The checks a printer would make: contrast, distance to
 *                       the trim, an empty signature, a name that had to shrink.
 *
 * Everything here is a SUGGESTION. Nothing applies itself, and every result
 * carries the confidence that produced it — a wrong guess that installs itself
 * silently is worse than no guess at all.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.CertSmart = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    /* ── 1. reading a certificate out of pasted text ─────────────────────────
     *
     * The structure of an award document is remarkably stable across languages
     * and decades, because it is a legal-ish form: an issuer, a big title, a
     * verb of awarding, a person, a reason, a place and date, a signature. The
     * ORDER is stable too. So the parser does not need to understand the text —
     * it needs to recognise the landmarks and split at them.
     *
     * Lines are classified independently and then reconciled, because a line's
     * own shape (ALL CAPS, one word, ends with a comma, contains a year) says
     * more than its position does. Position only breaks ties.
     */

    /* ⚠️⚠️ NO `\b` ANYWHERE IN THIS FILE. JavaScript's word boundary is defined
     * over [A-Za-z0-9_] ONLY, so between a space and «н» there is no boundary
     * at all and `/(грамота)\b/` never matches Russian. The first version had it
     * on this very line, and the consequence was invisible: every test still
     * found the title, because they were written in CAPITALS and the all-caps
     * heading fallback caught them. A normally-cased «Грамота» was missed
     * outright (found 2026-08-23 by a single-line paste that had nothing else
     * to fall back on). Boundaries are written out as character classes. */
    var EDGE = '(?=$|[\\s.,:;!?»"\')\\]—–\\-])';
    var TITLES = new RegExp('^(почётная грамота|почетная грамота|грамота|диплом|сертификат|благодарственное письмо|благодарность|свидетельство|похвальный лист|certificate of achievement|certificate|diploma|award|attestation|urkunde|diplom|zertifikat|certificado|certificat|sertifikat|piagam|상장|표창장|수료증|감사장|شهادة|دبلوم)' + EDGE, 'i');

    var LEADS = /^(награжда[ею]тся|награждён|награжден|вручается|выдан[оа]?|настоящ(им|ая|ий)|удостоверяет|подтверждает|за\s|благодарим|выражаем|объявляется|присуждается|is awarded|awarded to|presented to|this is to certify|hereby|verliehen an|wird verliehen|se otorga|otorgado a|décerné à|remis à|atribuído a|diberikan kepada|이 (상장|증서)|위 (사람|학생)|تُمنح|يُمنح|صادرة)/i;

    var REASONS = /^(за\s|в связи|по итогам|об участии|о прохождении|for\s|in recognition|für\s|por\s|pour\s|atas\s|بمناسبة|لـ)/i;

    var ROLES = /(директор|заведующ|начальник|руководител|председател|ректор|декан|генеральный|глава|мэр|принципал|тренер|учител|воспитател|организатор|principal|director|head ?teacher|chair|president|ceo|manager|coach|schulleiter|directeur|directora|diretor|kepala|교장|원장|대표|المدير|رئيس)/i;

    var ORG = /(школ|гимнази|лице[йя]|детский сад|садик|университет|институт|колледж|техникум|академи|центр|клуб|студи|компани|ооо|зао|оао|(?:^|[^\p{L}\p{N}_])ип(?![\p{L}\p{N}_])|фонд|ассоциаци|федераци|министерств|управлени|комитет|администраци|school|college|university|institute|academy|centre|center|studio|club|ltd|llc|inc|gmbh|s\.?a\.?|company|foundation|association|federation|ministry)/iu;
    /* «ип» — целым словом: \b в JavaScript знает только латиницу, и прежнее «ип\b»
       не находило «ИП Иванов» никогда; без левой границы оно нашло бы «клип». */

    var CITY = /^(г\.|гор\.|город|с\.|пос\.|пгт|станица|аул|д\.)\s*[А-ЯЁA-Z]/;

    /* ⚠️ ORDER IS THE RULE HERE, not decoration. A regex alternation returns the
     * FIRST branch that matches at the earliest position, so a bare-year branch
     * placed early swallows "2026" out of "May 25, 2026" and leaves "Boston,
     * May 25" behind as the place (measured 2026-08-23). Longest forms first,
     * bare year last.
     *
     * The decorations travel WITH the date: «25» and the trailing «г.» are part
     * of how a Russian document writes it, and stripping them mid-parse is how
     * «1» июня 2026» and «г. Москва, г.» happened. */
    var MONTH_RU = '[а-яё]{3,12}';
    var MONTH_EN = '[a-zà-ÿ]{3,12}';
    var DATE = new RegExp(
        '(' +
        // «25» мая 2026 г.  /  25 мая 2026 года
        '[«"\'(]?\\d{1,2}[»"\')]?\\s+' + MONTH_RU + '\\.?\\s+\\d{4}(\\s*(г\\.|года))?' +
        '|' +
        // May 25, 2026  /  25 May 2026
        '(' + MONTH_EN + '\\s+\\d{1,2},?\\s+\\d{4}|\\d{1,2}\\s+' + MONTH_EN + ',?\\s+\\d{4})' +
        '|' +
        // 25.05.2026 / 25/05/26
        '\\d{1,2}\\s*[.\\/-]\\s*\\d{1,2}\\s*[.\\/-]\\s*\\d{2,4}' +
        '|' +
        // a bare year, only when it is the whole tail of the line
        '(19|20)\\d{2}\\s*(г\\.|года)?\\s*$' +
        ')', 'i');

    var NUMBER = /(^|\s)(№|no\.?|nr\.?|#|рег\.?\s*№|регистрационный)\s*[\w\-\/]+/i;

    /* A person: two or three capitalised words, no digits, not an organisation.
     * Deliberately loose — the FIO engine is the authority, this only has to
     * pick the right LINE. */
    function looksLikeName(line) {
        var s = line.trim().replace(/[«»"'(),]/g, ' ').trim();
        if (!s || /\d/.test(s)) return 0;
        if (ORG.test(s)) return 0;
        var words = s.split(/\s+/);
        if (words.length < 2 || words.length > 4) return 0;
        var caps = words.filter(function (w) {
            return /^[А-ЯЁA-ZÀ-ÞŠŽЀ-Я]/.test(w) && w.length > 1;
        }).length;
        if (caps < words.length) return 0;
        /* All-caps in a short line is more likely a heading than a name. */
        if (s === s.toUpperCase() && s.length < 22) return 0.4;
        return 0.9;
    }

    function isAllCapsHeading(line) {
        var s = line.trim();
        if (s.length < 4 || s.length > 40) return false;
        var letters = s.replace(/[^\p{L}]/gu, '');
        if (!letters) return false;
        return letters === letters.toUpperCase() && letters.length >= 4;
    }

    /* Spaced-out display type — «Г Р А М О Т А» — is a title that has been
     * copied out of a PDF, and it must collapse back before anything else can
     * recognise it. */
    function unspace(line) {
        var s = line.trim();
        if (!/^(\S\s){3,}\S$/.test(s)) return line;
        if (s.replace(/\s/g, '').length > 24) return line;
        return s.replace(/\s+/g, '');
    }

    /* A wrapped continuation: the line before did not finish a sentence, and
     * this one does not open a new landmark. Conservative on purpose — joining
     * a signature onto a reason is worse than leaving a line unplaced. */
    function continuesProse(prev, next) {
        if (!next || next.length < 3) return false;
        if (/[.!?:;]$/.test(prev)) return false;
        if (TITLES.test(next) || LEADS.test(next) || ROLES.test(next) || DATE.test(next)) return false;
        if (NUMBER.test(next) || CITY.test(next)) return false;
        if (isAllCapsHeading(next)) return false;
        if (looksLikeName(next) > 0.5) return false;
        return true;
    }

    /* Cuts a single-line paste at its landmarks. Returns the original in a
     * one-element array when there is nothing convincing to cut at. */
    function splitOneLine(line) {
        /* `after: true` means the marker becomes a line of its OWN — that is
         * what turns «награждается» into a lead-in instead of a prefix glued to
         * the name. A reason marker is the opposite: «за» must stay attached to
         * what it introduces, or the reason arrives as the single word «за». */
        var marks = [
            { re: new RegExp('(^|[\\s(«"\'])(награжда[ею]тся|вручается|выдан[оа]?|awarded to|presented to|se otorga|verliehen an)' + EDGE, 'i'), after: true },
            { re: /(^|[\s(«"'])(за |for |für |por |pour )/i, after: false }
        ];
        var cuts = [0];
        marks.forEach(function (mk) {
            var m = line.match(mk.re);
            if (!m) return;
            /* The patterns capture the character in front of the marker so they
               can express a boundary without `\b`; the cut has to skip it, or
               the piece starts with a space and the next one loses a letter. */
            var lead = m[0].length - m[0].replace(/^[\s(«"']+/, '').length;
            /* Cut BEFORE the marker and again AFTER it, so the marker becomes
               its own line — that is what makes it a lead-in rather than a
               prefix stuck to the name. */
            cuts.push(m.index + lead);
            if (mk.after) cuts.push(m.index + m[0].length);
        });
        var title = line.match(TITLES);
        if (title && title.index === 0) cuts.push(title[0].length);
        cuts = cuts.filter(function (v, i, a) { return v > 0 && v < line.length && a.indexOf(v) === i; })
            .sort(function (a, b) { return a - b; });
        if (!cuts.length) return [line];
        var parts = [], prev = 0;
        cuts.concat([line.length]).forEach(function (c) {
            var piece = line.slice(prev, c).trim();
            if (piece) parts.push(piece);
            prev = c;
        });
        return parts.length > 1 ? parts : [line];
    }

    /**
     * parseCertificate(text) → { fields: {...}, confidence: 0..1, used: [...] }
     *
     * Never throws, never returns junk in a field it is unsure about: a field
     * it cannot place stays absent, and the caller keeps whatever was there.
     */
    function parseCertificate(text) {
        var raw = String(text || '').replace(/\r\n?/g, '\n');
        /* ⚠️ TWO copies of every line, and the difference is load-bearing.
         * `lines` is normalised for matching; `rawLines` keeps the original
         * spacing, because in a document «Директор школы    А. П. Смирнова» is
         * TWO COLUMNS held apart by nothing but those spaces. Collapsing them
         * first — as the first version did — makes the role and the signatory
         * one string with no seam left to split on. */
        var rawLines = [], lines = [];
        raw.split('\n').forEach(function (l) {
            var u = unspace(l);
            var norm = u.replace(/[ \t]+/g, ' ').trim();
            if (!norm) return;
            rawLines.push(u.replace(/\t/g, '    ').replace(/\s+$/, ''));
            lines.push(norm);
        });

        var out = { fields: {}, used: [], confidence: 0 };
        if (!lines.length) return out;

        /* A certificate pasted out of a message arrives as ONE line —
         * «Грамота награждается Иванов Иван Иванович за отличную учёбу». The
         * landmarks are all still there, just without newlines between them, so
         * the line is cut AT them and the rest of the parser proceeds as usual.
         * Only attempted on a single line: inside a multi-line document these
         * words appear mid-sentence and cutting there would shred it. */
        if (lines.length === 1 && lines[0].length > 24) {
            var one = splitOneLine(lines[0]);
            if (one.length > 1) { lines = one; rawLines = one.slice(); }
        }

        var taken = new Array(lines.length).fill(false);
        function take(i, field, value) {
            if (taken[i] || out.fields[field] != null) return false;
            taken[i] = true;
            out.fields[field] = value == null ? lines[i] : value;
            out.used.push({ line: i, field: field });
            return true;
        }

        var i;

        /* 1. Title — the strongest landmark. Prefer an exact known word; fall
         *    back to the first all-caps heading in the top half. */
        for (i = 0; i < lines.length; i++) {
            if (!taken[i] && TITLES.test(lines[i]) && lines[i].length < 46) { take(i, 'title'); break; }
        }
        if (out.fields.title == null) {
            for (i = 0; i < Math.ceil(lines.length / 2); i++) {
                if (!taken[i] && isAllCapsHeading(lines[i])) { take(i, 'title'); break; }
            }
        }
        var titleAt = out.fields.title != null ? out.used[out.used.length - 1].line : -1;

        /* 2. Organisation — above the title if anything is, else the first line
         *    that names an institution. */
        for (i = 0; i < lines.length; i++) {
            if (taken[i]) continue;
            if (ORG.test(lines[i]) && lines[i].length > 6) { take(i, 'org'); break; }
        }
        if (out.fields.org == null && titleAt > 0) take(0, 'org');

        /* 2b. Subtitle — «I степени», «за I место», «First place». It is short,
         *     it sits immediately under the title, and it is not the lead-in. */
        if (titleAt >= 0 && titleAt + 1 < lines.length && !taken[titleAt + 1]) {
            var sub = lines[titleAt + 1];
            if (sub.length <= 28 && !LEADS.test(sub) && !ORG.test(sub) &&
                !DATE.test(sub) && !NUMBER.test(sub) && looksLikeName(sub) < 0.5) {
                take(titleAt + 1, 'subtitle');
            }
        }

        /* 3. Lead-in — the verb of awarding. It is the anchor for the name:
         *    the recipient is almost always the line right after it. */
        var leadAt = -1;
        for (i = 0; i < lines.length; i++) {
            if (taken[i]) continue;
            if (LEADS.test(lines[i]) && lines[i].length < 60 && !REASONS.test(lines[i])) {
                take(i, 'lead'); leadAt = i; break;
            }
        }

        /* 4. Name. Right after the lead-in wins; otherwise the best-scoring
         *    line in the middle of the document. */
        if (leadAt >= 0 && leadAt + 1 < lines.length && !taken[leadAt + 1] && looksLikeName(lines[leadAt + 1]) > 0.5) {
            take(leadAt + 1, 'name');
        } else {
            var best = -1, bestScore = 0.55;
            for (i = 0; i < lines.length; i++) {
                if (taken[i]) continue;
                var sc = looksLikeName(lines[i]);
                /* Middle of the sheet is where a recipient lives. */
                if (i > 0 && i < lines.length - 1) sc += 0.05;
                if (sc > bestScore) { bestScore = sc; best = i; }
            }
            if (best >= 0) take(best, 'name');
        }
        var nameAt = out.fields.name != null ? out.used[out.used.length - 1].line : -1;

        /* 5. Reason — a line starting with «за …», or the longest remaining
         *    prose line after the name. A reason is prose and prose WRAPS: in a
         *    pasted document it is routinely two or three lines, and taking
         *    only the first leaves «и примерное поведение» orphaned on the
         *    sheet (measured 2026-08-23). Continuations are joined while the
         *    previous line does not end a sentence and the next does not start
         *    a new landmark. */
        for (i = 0; i < lines.length; i++) {
            if (taken[i]) continue;
            if (REASONS.test(lines[i]) && lines[i].length > 8) {
                var acc = lines[i];
                taken[i] = true;
                var j = i + 1;
                while (j < lines.length && !taken[j] && continuesProse(acc, lines[j])) {
                    acc += ' ' + lines[j];
                    taken[j] = true;
                    j++;
                }
                out.fields.reason = acc;
                out.used.push({ line: i, field: 'reason' });
                break;
            }
        }
        /* ⚠️ No landmarks, no guessing. Without a title AND without a name this
         * is not an award document, and picking "the longest line" out of a
         * chat message and calling it the reason is the parser inventing a
         * document that was never pasted. */
        if (out.fields.reason == null && (out.fields.title != null || out.fields.name != null)) {
            var longest = -1, len = 24;
            for (i = Math.max(0, nameAt + 1); i < lines.length; i++) {
                if (taken[i] || ROLES.test(lines[i]) || DATE.test(lines[i])) continue;
                if (lines[i].length > len) { len = lines[i].length; longest = i; }
            }
            if (longest >= 0) take(longest, 'reason');
        }

        /* 6. Detail — a short qualifier immediately after the name («ученик
         *    7 «А» класса»). Only there; anywhere else it is something else. */
        if (nameAt >= 0 && nameAt + 1 < lines.length && !taken[nameAt + 1]) {
            var d = lines[nameAt + 1];
            if (d.length < 60 && !REASONS.test(d) && !DATE.test(d) && !ROLES.test(d)) take(nameAt + 1, 'detail');
        }

        /* 7. Place and date. They usually share a line; split when they do. */
        for (i = lines.length - 1; i >= 0; i--) {
            if (taken[i]) continue;
            var m = lines[i].match(DATE);
            if (!m) continue;
            var whole = lines[i];
            /* The match already carries its own guillemets and its trailing
               «г.» (see the DATE comment), so the date is taken VERBATIM and
               the remainder is whatever is left once it is cut out. Trimming
               the decorations here is what produced «1» июня 2026» and left a
               stray «г.» sitting in the place. */
            var date = m[0].trim();
            var rest = (whole.slice(0, m.index) + ' ' + whole.slice(m.index + m[0].length))
                .replace(/\s+/g, ' ')
                .replace(/^[\s,;·—–\-]+/, '')
                .replace(/[\s,;·—–\-]+$/, '')
                .replace(/\s+(г\.|года)$/i, '')
                .trim();
            take(i, 'date', date);
            if (rest && (CITY.test(rest) || rest.length < 34) && out.fields.place == null) {
                out.fields.place = rest;
            }
            break;
        }
        if (out.fields.place == null) {
            for (i = lines.length - 1; i >= 0; i--) {
                if (!taken[i] && CITY.test(lines[i])) { take(i, 'place'); break; }
            }
        }

        /* 8. Signature block: a role, and the signatory beside or under it.
         *    Split on the RAW line — see the note where rawLines is built. Two
         *    seams count: a run of spaces (a column), and an initials group
         *    («А. П. Смирнова», «J. R. Hale»), which is a name even when only
         *    one space separates it from the role. */
        var INITIALS = /([А-ЯЁA-Z]\.\s?[А-ЯЁA-Z]?\.?\s?[А-ЯЁA-Z][а-яёa-z\u00c0-\u024f]+)\s*$/;
        for (i = lines.length - 1; i >= 0; i--) {
            if (taken[i] || !ROLES.test(lines[i])) continue;
            var rawLine = rawLines[i];
            var byColumn = rawLine.match(/^(.*?\S)(?:\s{2,}|\s+[—–\-]\s+)(\S.*)$/);
            var byInitials = lines[i].match(INITIALS);
            if (byColumn && (looksLikeName(byColumn[2]) > 0 || INITIALS.test(byColumn[2].trim() + ' '))) {
                take(i, 'role', byColumn[1].trim());
                out.fields.signName = byColumn[2].trim();
            } else if (byInitials && byInitials.index > 2) {
                take(i, 'role', lines[i].slice(0, byInitials.index).replace(/[\s—–\-,]+$/, ''));
                out.fields.signName = byInitials[1].trim();
            } else {
                take(i, 'role');
                if (i + 1 < lines.length && !taken[i + 1] && looksLikeName(lines[i + 1]) > 0.3) {
                    take(i + 1, 'signName');
                } else if (i > 0 && !taken[i - 1] && /[А-ЯЁA-Z]\.\s*[А-ЯЁA-Z]\./.test(lines[i - 1])) {
                    take(i - 1, 'signName');
                }
            }
            break;
        }

        /* 9. Registration number, anywhere. */
        for (i = 0; i < lines.length; i++) {
            if (taken[i]) continue;
            if (NUMBER.test(lines[i]) && lines[i].length < 40) { take(i, 'number'); break; }
        }

        /* Confidence: how much of the document we placed, weighted by whether
         * the two landmarks that matter (a title and a person) were found. */
        var placed = Object.keys(out.fields).length;
        var coverage = out.used.length / lines.length;
        out.confidence = Math.min(1,
            (out.fields.title ? 0.3 : 0) +
            (out.fields.name ? 0.3 : 0) +
            Math.min(0.25, placed * 0.04) +
            Math.min(0.15, coverage * 0.2));
        out.leftover = lines.filter(function (_, k) { return !taken[k]; });
        return out;
    }

    /* ── 2. a palette out of an uploaded crest ───────────────────────────────
     *
     * A school's colours are already in its crest; asking for a hex code is
     * asking the wrong question. Sampling is done on a small canvas because at
     * 64 px the answer is the same and the cost is not.
     */
    function paletteFrom(img, opts) {
        opts = opts || {};
        var N = 64;
        var cv = document.createElement('canvas');
        cv.width = N; cv.height = N;
        var c = cv.getContext('2d', { willReadFrequently: true });
        try {
            c.drawImage(img, 0, 0, N, N);
            var px = c.getImageData(0, 0, N, N).data;
            var buckets = {};
            for (var i = 0; i < px.length; i += 4) {
                var a = px[i + 3];
                if (a < 200) continue;                      // transparent → not a colour
                var r = px[i], g = px[i + 1], b = px[i + 2];
                var mx = Math.max(r, g, b), mn = Math.min(r, g, b);
                var lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
                var sat = mx === 0 ? 0 : (mx - mn) / mx;
                /* Paper and outline are not the brand. */
                if (lum > 0.93 || lum < 0.05) continue;
                if (sat < 0.12 && lum > 0.6) continue;
                var key = (r >> 4) + ',' + (g >> 4) + ',' + (b >> 4);
                var e = buckets[key] || (buckets[key] = { n: 0, r: 0, g: 0, b: 0, sat: 0, lum: 0 });
                e.n++; e.r += r; e.g += g; e.b += b; e.sat += sat; e.lum += lum;
            }
            var list = Object.keys(buckets).map(function (k) {
                var e = buckets[k];
                return {
                    r: Math.round(e.r / e.n), g: Math.round(e.g / e.n), b: Math.round(e.b / e.n),
                    n: e.n, sat: e.sat / e.n, lum: e.lum / e.n
                };
            });
            if (!list.length) return null;

            /* INK is the dark, present colour — the one the eye reads as "the
             * colour of this crest". ACCENT is the most saturated one that is
             * not the ink. Ranking by count alone returns the background. */
            var ink = list.slice().sort(function (a, b) {
                return (b.n * (1 - b.lum) * (0.4 + b.sat)) - (a.n * (1 - a.lum) * (0.4 + a.sat));
            })[0];
            var accent = list.slice().sort(function (a, b) {
                return (b.n * b.sat * (0.35 + b.lum)) - (a.n * a.sat * (0.35 + a.lum));
            })[0];

            function hex(o) {
                return '#' + [o.r, o.g, o.b].map(function (v) {
                    return ('0' + Math.max(0, Math.min(255, v)).toString(16)).slice(-2);
                }).join('');
            }
            /* An ink that is not dark enough to read as text is darkened rather
             * than rejected: the crest's hue is the point, its lightness is not. */
            var inkHex = hex(darken(ink, 0.42));
            var accHex = hex(accent === ink ? lighten(ink, 0.62) : accent);
            return {
                ink: inkHex,
                accent: accHex,
                paper: hex(tintPaper(accent)),
                sampled: list.length
            };
        } catch (e) {
            /* A tainted canvas (cross-origin image) throws on getImageData —
               there is no palette to offer, and that is a fine answer. */
            return null;
        }
    }

    function darken(o, targetLum) {
        var lum = (0.2126 * o.r + 0.7152 * o.g + 0.0722 * o.b) / 255;
        if (lum <= targetLum) return o;
        var k = targetLum / Math.max(0.001, lum);
        return { r: o.r * k, g: o.g * k, b: o.b * k };
    }
    function lighten(o, targetLum) {
        var lum = (0.2126 * o.r + 0.7152 * o.g + 0.0722 * o.b) / 255;
        if (lum >= targetLum) return o;
        var t = (targetLum - lum) / Math.max(0.001, 1 - lum);
        return { r: o.r + (255 - o.r) * t, g: o.g + (255 - o.g) * t, b: o.b + (255 - o.b) * t };
    }
    /* Paper takes a whisper of the accent's hue — 4 % — which is the difference
     * between "white" and "this document has a colour scheme". */
    function tintPaper(o) {
        return { r: 255 - (255 - o.r) * 0.045, g: 255 - (255 - o.g) * 0.045, b: 255 - (255 - o.b) * 0.045 };
    }

    /* ── 3. preflight ────────────────────────────────────────────────────────
     *
     * What a printer would tell you before running 30 sheets. Every item is
     * something the person can act on, and nothing here is stylistic taste.
     */
    /* ── what is underneath a line of type ───────────────────────────────────
     *
     * The answer has three layers, and all three are already IN the primitive
     * list, so this reads the artefact rather than re-deriving it from the
     * document: the paper, then the illustration, then the scrim laid on top of
     * it. A picture the author uploaded cannot be read from primitives at all —
     * its luminance grid is measured ONCE at upload and travels in the document
     * (`style.bgImageLuma`), the same trick as the aspect ratio, so this stays
     * deterministic and testable with no canvas in reach. */

    /** Generous bounding box in millimetres. `path` is approximated from its
     *  coordinate pairs — control points bound the curve, so the box can only
     *  come out too big, which errs toward warning. That is the right
     *  direction: a false «проверьте» costs a glance, a false «всё в порядке»
     *  costs the print run. */
    function bboxOf(p) {
        if (p.type === 'rect' || p.type === 'image') {
            return { x0: p.x, y0: p.y, x1: p.x + p.w, y1: p.y + p.h };
        }
        if (p.type === 'circle') {
            return { x0: p.cx - p.r, y0: p.cy - p.r, x1: p.cx + p.r, y1: p.cy + p.r };
        }
        if (p.type === 'path' && p.d) {
            var nums = String(p.d).match(/-?\d*\.?\d+/g);
            if (!nums || nums.length < 2) return null;
            var x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
            for (var i = 0; i + 1 < nums.length; i += 2) {
                var x = +nums[i], y = +nums[i + 1];
                if (x < x0) x0 = x; if (x > x1) x1 = x;
                if (y < y0) y0 = y; if (y > y1) y1 = y;
            }
            return isFinite(x0) ? { x0: x0, y0: y0, x1: x1, y1: y1 } : null;
        }
        return null;
    }

    function overlaps(a, b) {
        return a && b && a.x0 < b.x1 && a.x1 > b.x0 && a.y0 < b.y1 && a.y1 > b.y0;
    }

    /** src over dst at `alpha`, both '#rrggbb'. */
    function over(dst, src, alpha) {
        function px(h) {
            h = String(h || '#000').replace('#', '');
            if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
            return [parseInt(h.substr(0, 2), 16) || 0, parseInt(h.substr(2, 2), 16) || 0, parseInt(h.substr(4, 2), 16) || 0];
        }
        var d = px(dst), s = px(src), a = Math.max(0, Math.min(1, alpha == null ? 1 : alpha));
        return '#' + [0, 1, 2].map(function (i) {
            return ('0' + Math.round(d[i] * (1 - a) + s[i] * a).toString(16)).slice(-2);
        }).join('');
    }

    function backdropIndex(result, doc, paper) {
        var st = (doc && doc.style) || {};
        var art = [], scrim = null;
        (result.prims || []).forEach(function (p) {
            if (p.scrim) { scrim = { box: bboxOf(p), fill: p.fill, alpha: p.opacity == null ? 1 : p.opacity }; return; }
            if (!p.background) return;
            art.push({ box: bboxOf(p), fill: p.fill || p.color, alpha: p.opacity == null ? 1 : p.opacity, image: p.type === 'image' });
        });
        return { art: art, scrim: scrim, luma: st.bgImageLuma || null, page: result.page, paper: paper };
    }

    /** The box the glyphs of one line actually occupy, from the anchor the
     *  primitive publishes. Same vocabulary the editor hit-tests with. */
    function textBox(p) {
        var w = p.w || p.size * 2;
        var x = p.anchor === 'middle' ? p.x - w / 2 : p.anchor === 'end' ? p.x - w : p.x;
        return { x0: x, y0: p.y - p.size * 0.8, x1: x + w, y1: p.y + p.size * 0.35 };
    }

    /**
     * The colour a line of type is actually set on, and whether art contributed.
     *
     * ⚠️ For a photograph the WORST cell wins, and "worst" is decided by the
     * contrast with THIS ink — not by the darkest pixel. A white name over a
     * photo fails on the light cells, a dark one on the dark cells, and a rule
     * that only looked for dark would pass the white name over snow.
     */
    function backdropUnder(p, idx, paper) {
        var t = textBox(p);
        var color = paper, fromArt = false;
        for (var i = 0; i < idx.art.length; i++) {
            var a = idx.art[i];
            if (!overlaps(t, a.box)) continue;
            if (a.image) {
                var g = idx.luma;
                if (!g || !g.v || !g.v.length || !idx.page) { fromArt = true; continue; }
                var worstC = null, worstV = null;
                for (var r = 0; r < g.rows; r++) {
                    for (var c = 0; c < g.cols; c++) {
                        var cx0 = c / g.cols * idx.page.w, cx1 = (c + 1) / g.cols * idx.page.w;
                        var cy0 = r / g.rows * idx.page.h, cy1 = (r + 1) / g.rows * idx.page.h;
                        if (!overlaps(t, { x0: cx0, y0: cy0, x1: cx1, y1: cy1 })) continue;
                        var v = g.v[r * g.cols + c];
                        var hx = ('0' + Math.round(v).toString(16)).slice(-2);
                        var cand = contrast(p.color, over(color, '#' + hx + hx + hx, a.alpha));
                        if (worstC == null || cand < worstC) { worstC = cand; worstV = v; }
                    }
                }
                if (worstV != null) {
                    var wh = ('0' + Math.round(worstV).toString(16)).slice(-2);
                    color = over(color, '#' + wh + wh + wh, a.alpha);
                    fromArt = true;
                }
                continue;
            }
            if (!a.fill) continue;      // a hairline stroke does not darken a page
            color = over(color, a.fill, a.alpha);
            fromArt = true;
        }
        if (idx.scrim && overlaps(t, idx.scrim.box)) {
            color = over(color, idx.scrim.fill, idx.scrim.alpha);
        }
        return { color: color, fromArt: fromArt };
    }

    function contrast(a, b) {
        function lin(h) {
            h = String(h || '#000').replace('#', '');
            if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
            var v = [0, 2, 4].map(function (i) {
                var c = parseInt(h.substr(i, 2), 16) / 255;
                return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
            });
            return 0.2126 * v[0] + 0.7152 * v[1] + 0.0722 * v[2];
        }
        var l1 = lin(a), l2 = lin(b);
        return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
    }

    /**
     * preflight(result, doc, opts) → [{code, level, block?, value?}]
     * `level`: 'stop' (will print wrong) | 'warn' (worth a look) | 'ok'.
     */
    function preflight(result, doc, opts) {
        opts = opts || {};
        var out = [];
        var page = result.page;
        var paper = (doc.style && doc.style.paper) || '#ffffff';
        if (paper === 'none') paper = '#ffffff';

        /* Ink on WHAT IS ACTUALLY BEHIND IT. 4.5:1 is the readable-text
         * threshold; a certificate is large type, so 3:1 is the honest floor and
         * below that it is a real problem rather than a preference.
         *
         * ⚠️ This used to compare the ink to `style.paper` — to a flat colour —
         * and that was fine for exactly as long as the sheet had nothing on it.
         * The moment an illustration or a photograph goes underneath, comparing
         * against the paper answers a question nobody asked: it reports «всё в
         * порядке — можно печатать» while a dark name crosses a dark branch.
         * A checker that cannot see the thing it is checking is worse than no
         * checker, because it ends the conversation. */
        var backdrops = backdropIndex(result, doc, paper);
        result.prims.forEach(function (p) {
            if (p.type !== 'text' || !p.s.trim()) return;
            var behind = backdropUnder(p, backdrops, paper);
            var ratio = contrast(p.color, behind.color);
            var code = behind.fromArt ? 'contrast_bg' : 'contrast';
            if (ratio < 2.2) out.push({ code: code, level: 'stop', text: p.s, value: Math.round(ratio * 10) / 10 });
            else if (ratio < 3) out.push({ code: code, level: 'warn', text: p.s, value: Math.round(ratio * 10) / 10 });
        });

        /* ── a script with no face behind it ────────────────────────────────
         *
         * ⚠️ MEASURED 2026-08-24, and it is worse than anything else this
         * checker looks for: Hebrew, Thai, Devanagari, Georgian and Armenian
         * have no covering face, so `planRuns` hands back `faceId: null`, the
         * exporter falls back to a Latin face, and EVERY CHARACTER OF THE NAME
         * IS ABSENT from the PDF. Not reordered — gone. The screen shows it
         * (the browser has system fonts), the preflight said «всё в порядке»,
         * and the sheet printed with a blank where the person's name was.
         *
         * We do not ship faces for those scripts, and guessing which ones
         * matter is not something a certificate tool should do speculatively.
         * What it can do is stop lying: say which characters will not print.
         *
         * The coverage test is injected, because it lives in the font layer and
         * this file has to keep running in the Node tests with no network. */
        if (typeof opts.uncovered === 'function') {
            var bad = {};
            result.prims.forEach(function (p2) {
                if (p2.type !== 'text' || !String(p2.s || '').trim()) return;
                (opts.uncovered(p2.s) || []).forEach(function (c) { bad[c] = true; });
            });
            var chars = Object.keys(bad);
            if (chars.length) {
                out.push({ code: 'no_font', level: 'stop',
                    text: chars.slice(0, 12).join(' '), value: chars.length });
            }
        }

        /* The non-printable margin. Every consumer printer eats 3–5 mm; ink
         * placed there looks perfect on screen and comes out shaved. */
        var MARGIN = 5;
        var tight = 0;
        result.prims.forEach(function (p) {
            var xs = [], ys = [];
            /* ⚠️ A background is SUPPOSED to reach the edge — that is what bleed
               is. Counting it turned the useful warning into a permanent one:
               after a full-page picture the check reported «1 элемент ближе
               5 мм» on every sheet, for ever, and a warning that never clears
               is a warning people learn to dismiss. */
            if (p.background || p.scrim) return;
            if (p.type === 'text') {
                var half = (p.w || 0) / (p.anchor === 'middle' ? 2 : 1);
                xs = [p.anchor === 'start' ? p.x : p.x - half, p.anchor === 'end' ? p.x : p.x + half];
                ys = [p.y - p.size, p.y + p.size * 0.3];
            } else if (p.type === 'image') {
                xs = [p.x, p.x + p.w]; ys = [p.y, p.y + p.h];
            } else return;
            if (Math.min.apply(null, xs) < MARGIN || Math.max.apply(null, xs) > page.w - MARGIN ||
                Math.min.apply(null, ys) < MARGIN || Math.max.apply(null, ys) > page.h - MARGIN) tight++;
        });
        if (tight) out.push({ code: 'near_edge', level: 'warn', value: tight });

        /* Overlap between two text blocks — the thing dragging makes easy and
         * the eye forgives on screen at 40 % zoom. */
        var boxes = result.prims.filter(function (p) { return p.type === 'text' && p.s.trim(); })
            .map(function (p) {
                var half = (p.w || 0) / (p.anchor === 'middle' ? 2 : 1);
                return {
                    s: p.s,
                    x0: p.anchor === 'start' ? p.x : p.x - half,
                    x1: p.anchor === 'end' ? p.x : p.x + half,
                    y0: p.y - p.size * 0.9, y1: p.y + p.size * 0.25
                };
            });
        for (var a = 0; a < boxes.length; a++) {
            for (var b = a + 1; b < boxes.length; b++) {
                var A = boxes[a], B = boxes[b];
                var ox = Math.min(A.x1, B.x1) - Math.max(A.x0, B.x0);
                var oy = Math.min(A.y1, B.y1) - Math.max(A.y0, B.y0);
                if (ox > 1.5 && oy > 1.0) {
                    out.push({ code: 'overlap', level: 'stop', text: A.s + ' / ' + B.s });
                    a = boxes.length;                       // one report is enough
                    break;
                }
            }
        }

        /* Anything the layout itself could not honour. */
        (result.warnings || []).forEach(function (w) {
            if (w.code === 'overflow') out.push({ code: 'overflow', level: 'stop', block: w.block, text: w.text });
            else if (w.code === 'page_overflow') out.push({ code: 'page_overflow', level: 'stop', value: w.over });
        });

        /* An unsigned award document. Not an error — some are issued unsigned —
         * but it is the omission people discover after printing. */
        var hasSign = (doc.blocks || []).some(function (b) {
            return b.kind === 'row' && (b.cells || []).some(function (c) {
                return c.kind === 'sign' && (String(c.role || '').trim() || String(c.name || '').trim());
            });
        });
        if (!hasSign) out.push({ code: 'no_signature', level: 'warn' });

        return out;
    }

    /* ── 4. one press that changes the look ──────────────────────────────────
     *
     * NOT a random re-roll. Frames, palettes and type pair well or badly, and
     * shuffling all three independently produces something nobody would choose.
     * The table below is the set of combinations that were looked at; the
     * shuffle picks a row, never a cell.
     */
    var LOOKS = [
        { frame: 'classic',   pal: 'royal',    title: 'yeseva',     name: 'marck',      body: 'noto' },
        { frame: 'laurel',    pal: 'royal',    title: 'playfair',   name: 'playfair',   body: 'noto' },
        { frame: 'guilloche', pal: 'emerald',  title: 'yeseva',     name: 'playfair',   body: 'noto' },
        { frame: 'guilloche', pal: 'royal',    title: 'playfair',   name: 'marck',      body: 'noto' },
        { frame: 'deco',      pal: 'burgundy', title: 'oswald',     name: 'oswald',     body: 'montserrat' },
        { frame: 'deco',      pal: 'graphite', title: 'montserrat', name: 'montserrat', body: 'montserrat' },
        { frame: 'meander',   pal: 'emerald',  title: 'yeseva',     name: 'playfair',   body: 'noto' },
        { frame: 'meander',   pal: 'forest',   title: 'playfair',   name: 'playfair',   body: 'noto' },
        { frame: 'scroll',    pal: 'rose',     title: 'playfair',   name: 'marck',      body: 'noto' },
        { frame: 'scroll',    pal: 'burgundy', title: 'yeseva',     name: 'caveat',     body: 'noto' },
        { frame: 'braid',     pal: 'burgundy', title: 'playfair',   name: 'playfair',   body: 'noto' },
        { frame: 'wave',      pal: 'sky',      title: 'montserrat', name: 'playfair',   body: 'montserrat' },
        { frame: 'wave',      pal: 'graphite', title: 'montserrat', name: 'montserrat', body: 'montserrat' },
        { frame: 'corners',   pal: 'graphite', title: 'montserrat', name: 'montserrat', body: 'montserrat' },
        { frame: 'corners',   pal: 'sky',      title: 'oswald',     name: 'playfair',   body: 'montserrat' },
        { frame: 'kids',      pal: 'sunny',    title: 'comfortaa',  name: 'comfortaa',  body: 'comfortaa' },
        { frame: 'kids',      pal: 'sky',      title: 'comfortaa',  name: 'lobster',    body: 'comfortaa' },
        { frame: 'classic',   pal: 'mono',     title: 'playfair',   name: 'playfair',   body: 'noto' },
        { frame: 'classic',   pal: 'forest',   title: 'yeseva',     name: 'marck',      body: 'noto' },
        { frame: 'laurel',    pal: 'burgundy', title: 'yeseva',     name: 'marck',      body: 'noto' }
    ];

    /** nextLook(current, rnd) — the next combination, never the current one. */
    function nextLook(cur, rnd) {
        var pick = Math.floor((rnd == null ? Math.random() : rnd) * LOOKS.length);
        var same = function (a) {
            return a && cur && a.frame === cur.frame && a.pal === cur.pal && a.title === cur.title;
        };
        for (var i = 0; i < LOOKS.length; i++) {
            var cand = LOOKS[(pick + i) % LOOKS.length];
            if (!same(cand)) return cand;
        }
        return LOOKS[0];
    }

    return {
        parseCertificate: parseCertificate,
        paletteFrom: paletteFrom,
        preflight: preflight,
        contrast: contrast,
        nextLook: nextLook,
        LOOKS: LOOKS,
        _looksLikeName: looksLikeName,
        _unspace: unspace
    };
});
